import { Component, OnInit } from '@angular/core';
import { ReactiveFormsModule } from "@angular/forms";
import { BreadcrumbComponent } from "@shared/components/breadcrumb/breadcrumb.component";
import { MatIcon } from "@angular/material/icon";
import { MatTab, MatTabGroup, MatTabLabel } from "@angular/material/tabs";
import { MatDivider } from "@angular/material/divider";
import { CapitalizePipe } from "@core/pipes/capitalize.pipe";
import { NgClass, NgIf } from "@angular/common";
import { FeatherIconsComponent } from "@shared/components/feather-icons/feather-icons.component";
import { MatButton, MatIconButton } from "@angular/material/button";
import { IDetalleReceta } from "@core/interfaces/receta-data";
import { ActivatedRoute } from "@angular/router";
import { PrescriptionService } from "@core/http/table-data-services/prescription.service";
import { CatalogsService } from "@core/http/catalogs.service";
import { MatDialog } from "@angular/material/dialog";
import { GeneralFunctionsService } from "@core/service/generalFunctions.service";
import { MyPrescriptionReactionsComponent } from "../my-prescription-reactions/my-prescription-reactions.component";
import { HttpErrorResponse } from "@angular/common/http";
import { HttpMesageHandlerService } from "@core/service/http-mesage-handler";
import { AuthManagementService } from "@core/service/auth-management.service";
import { firstValueFrom } from 'rxjs';
import { CreateEditService } from '@core/http/create-edit-service';
import {downloadPrescriptionPdf, generatePrescriptionPdfBase64, openPrintWindow} from "@shared/utils/window.utils";
import {MatTooltipModule} from "@angular/material/tooltip";

@Component({
  selector: 'app-my-prescription-meds',
  standalone: true,
  imports: [
    BreadcrumbComponent,
    MatIcon,
    MatTab,
    MatTabGroup,
    MatTabLabel,
    ReactiveFormsModule,
    MatDivider,
    CapitalizePipe,
    NgClass,
    FeatherIconsComponent,
    MatIconButton,
    MatButton,
    NgIf,
    MatTooltipModule,
  ],
  templateUrl: './my-prescription-meds.component.html',
  styleUrl: './my-prescription-meds.component.scss'
})
export class MyPrescriptionMedsComponent implements OnInit {
  param: string | null = null;       // idReceta
  param2: string | null = null;      // fecha
  folio: string | null = null;
  estatusReceta: string | null = null;
  dowsloadpdf: string | null = null;

  /** idPaciente que viene en la ruta (par5) */
  idDelPaciente: string | null = null;

  userImage: string | null = null;
  detalles: IDetalleReceta[] = [];
  pin: string | null = null;

  private readonly isPatient: boolean;

  /** ✅ idPaciente real del paciente logueado (se resuelve una vez) */
  private loggedPacienteId: string | null = null;
  private loggedPacienteResolved = false;

  constructor(
    private route: ActivatedRoute,
    private service: PrescriptionService,
    private catalogsService: CatalogsService,
    public dialog: MatDialog,
    private gfs: GeneralFunctionsService,
    private httpMsgHandler: HttpMesageHandlerService,
    private authManagement: AuthManagementService,
    private ceService: CreateEditService
  ) {
    this.isPatient = this.authManagement.isUserPatient();

    this.route.paramMap.subscribe(params => {
      const param = params.get('param');
      this.param = param ? atob(param) : null;

      const param2 = params.get('par');
      this.param2 = param2 ? atob(param2) : null;

      const par2 = params.get('par2');
      this.folio = par2 ? atob(par2) : null;

      const par3 = params.get('par3');
      this.estatusReceta = par3 ? atob(par3) : null;

      const par4 = params.get('par4');
      this.dowsloadpdf = par4 ? atob(par4) : null;

      const par5 = params.get('par5');
      const decoded = par5 ? atob(par5) : null;
      this.idDelPaciente = decoded && decoded !== 'null' ? decoded : null;
    });
  }

  // =========================================================
  // ✅ Resuelve loggedPacienteId (igual que en la lista)
  // =========================================================
  private async ensureLoggedPacienteId(): Promise<void> {
    if (this.loggedPacienteResolved) return;

    const userData = this.authManagement.userData();
    const idUsuario = (userData?.IdUsuario ?? userData?.idUsuario ?? '').toString().trim();

    if (!idUsuario) {
      this.loggedPacienteId = null;
      this.loggedPacienteResolved = true;
      return;
    }

    try {
      const res: any = await firstValueFrom(this.ceService.getpatientByIdUser(idUsuario));
      const payload = res?.data ?? null;
      const row = Array.isArray(payload) ? payload[0] : payload;
      const idPaciente = (row?.idPaciente ?? row?.IdPaciente ?? '').toString().trim();

      this.loggedPacienteId = idPaciente || null;
      this.loggedPacienteResolved = true;
    } catch {
      this.loggedPacienteId = null;
      this.loggedPacienteResolved = true;
    }
  }

  // =========================================================
  // ✅ Decide qué idPaciente mandar al backend
  // =========================================================
  private resolveIdPacienteForBackend(): string | null {
    const routePaciente = (this.idDelPaciente ?? '').toString().trim();
    const hasRoutePaciente = !!routePaciente;

    // Médico/Admin => debe mandar idPaciente sí o sí
    if (!this.isPatient) {
      return hasRoutePaciente ? routePaciente : null;
    }

    // Paciente:
    // - Si el idPaciente de la ruta ES el mismo que el logueado => null (token mode)
    // - Si es diferente => tutor (o selección) => manda routePaciente
    const logged = (this.loggedPacienteId ?? '').toString().trim();

    if (!hasRoutePaciente) return null;       // no vino => token
    if (!logged) return routePaciente;        // no pude resolver => mejor mandar el de ruta
    if (routePaciente === logged) return null; // paciente normal => token

    return routePaciente; // tutor/selección => enviar
  }

  async ngOnInit(): Promise<void> {
    this.pin = (history.state?.pin ?? null);

    if (!this.param) return;

    // ✅ importante para comparar routePaciente vs loggedPacienteId
    await this.ensureLoggedPacienteId();

    this.getDetails(this.param);
  }

  getDetails(idReceta: string): void {
    const userData = this.authManagement.userData();
    this.userImage = userData?.Imagen
      ? 'data:image/png;base64,' + userData.Imagen
      : 'assets/images/user/imgUSer.png';

    const idPacienteToSend = this.resolveIdPacienteForBackend();

    this.service.getPrescriptionDetailsForPatient(idReceta, idPacienteToSend, this.pin).subscribe({
      next: (data) => {
        if (!data?.data) return;

        this.detalles = data.data.map(detalle => ({
          ...detalle,
          periodoInicio: (detalle.periodoInicio ?? '').split('T')[0],
          periodoTerminacion: (detalle.periodoTerminacion ?? '').split('T')[0],
        }));
      },
      error: (err: HttpErrorResponse) => this.httpMsgHandler.handleMsg(err),
    });
  }

  get canDownloadPdf(): boolean {
    return (this.dowsloadpdf ?? '').trim() === '1';
  }

  descargarPDF(): void {
    if (!this.detalles?.length) return;

    const idReceta = this.detalles[0].idReceta;
    const idPacienteToSend = this.resolveIdPacienteForBackend();

    this.catalogsService.getRecetaByIdReceta(idReceta, idPacienteToSend, this.pin ?? null).subscribe({
      next: (res: string) => void downloadPrescriptionPdf(res, `receta-${this.folio || idReceta}.pdf`),
      error: (err: HttpErrorResponse) => this.httpMsgHandler.handleMsg(err),
    });
  }

  enviarRecetaPorCorreo(): void {
    if (!this.detalles?.length) return;

    const idReceta = this.detalles[0].idReceta;
    const idPacienteToSend = this.resolveIdPacienteForBackend();

    this.catalogsService.getRecetaByIdReceta(idReceta, idPacienteToSend, this.pin ?? null).subscribe({
      next: async (res: string) => {
        try {
          const pdfBase64 = await generatePrescriptionPdfBase64(res, {
            title: 'Enviando receta...',
            subtitle: 'Estamos preparando el PDF para correo'
          });

          this.service.enviarRecetaPorCorreo({ idReceta, pdfBase64 }).subscribe({
            next: (response) => this.httpMsgHandler.handleMsg(response as any),
            error: (err: HttpErrorResponse) => this.httpMsgHandler.handleMsg(err),
          });
        } catch (error) {
          this.httpMsgHandler.handleMsg(error as HttpErrorResponse);
        }
      },
      error: (err: HttpErrorResponse) => this.httpMsgHandler.handleMsg(err),
    });
  }

  printHtml(fullHtmlString: string): void {
    const printWidth = 1000;
    const printHeight = 800;

    const screenWidth = window.screen.width || 1920;
    const screenHeight = window.screen.height || 1080;

    const left = Math.max((screenWidth - printWidth) / 2, 0);
    const top = Math.max((screenHeight - printHeight) / 2, 0);

    const features = `width=${printWidth},height=${printHeight},top=${top},left=${left}`;

    const printWindow = openPrintWindow(features);

    if (!printWindow) {
      return;
    }

    printWindow.document.open();
    printWindow.document.write(fullHtmlString);
    printWindow.document.close();

    printWindow.onload = () => {
      printWindow.print();
      printWindow.close();
    };
  }

  updateLine(line: IDetalleReceta): void {
    this.gfs.mayusAcentos(line);
    const dialogRef = this.dialog.open(MyPrescriptionReactionsComponent, {
      maxWidth: 'none',
      data: line,
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) this.ngOnInit();
    });
  }
}
